import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { SceneKey, ThemeRegistry } from '@classy/theme-sdk';
import { ThemeProvider } from './ThemeProvider.js';
import { BgLayer } from './BgLayer.js';
import { getClassroomThemeKey, setClassroomThemeKey } from './themeStorage.js';

export interface ClassroomBgProps {
  registry: ThemeRegistry;
  classroomId: string;
  /** Default theme to fall back to when this classroom has no stored choice. */
  fallbackKey?: string;
  allowedThemeKeys?: readonly string[] | undefined;
  /** Which scene the BgLayer should render. */
  scene?: SceneKey;
  children: ReactNode;
}

/**
 * Wraps a classroom-scoped subtree (ClassroomDetail, live morning-meeting)
 * in a ThemeProvider + BgLayer driven by the per-classroom theme key.
 *
 * Reads from localStorage on mount; persists picks made via the
 * ThemeProvider's onChange (e.g. ThemePicker on the General tab).
 * Until the backend per-classroom theme column lands, this is the
 * source of truth at the UI layer — see docs/CLASSROOM_HUB_PLAN.md.
 */
export function ClassroomBg({
  registry,
  classroomId,
  fallbackKey = 'jungle',
  allowedThemeKeys,
  scene = 'home',
  children,
}: ClassroomBgProps) {
  const [themeKey, setThemeKey] = useState(() => getClassroomThemeKey(classroomId, fallbackKey));

  // Re-pull when the classroom changes (drilling between classrooms in
  // the hub, or activating a different one from a card).
  useEffect(() => {
    setThemeKey(getClassroomThemeKey(classroomId, fallbackKey));
  }, [classroomId, fallbackKey]);

  const onChange = useCallback(
    (next: string) => {
      setThemeKey(next);
      setClassroomThemeKey(classroomId, next);
    },
    [classroomId],
  );

  return (
    <ThemeProvider
      registry={registry}
      themeKey={themeKey}
      fallbackKey={fallbackKey}
      allowedThemeKeys={allowedThemeKeys}
      onChange={onChange}
      setGlobalAccent
    >
      <BgLayer scene={scene} />
      {children}
    </ThemeProvider>
  );
}
